/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { trailCreate, trailUpdate, trailDelete, TRAIL_MODULES } from '../../utils/activityTrail';
import {
    Card, Button, Table, Modal, Select, message, Input,
    Tag, Row, Col, Checkbox, Progress, Divider,
    Tooltip, InputNumber, Spin, Badge, Alert, Radio
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, CalendarOutlined,
    ThunderboltOutlined, TeamOutlined, WarningFilled,
    CopyOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { usePeriods } from '../../hooks/usePeriods';
import FacilityService from '../../services/FacilityService';
import { AntdSelect, AntdDatepicker } from '../../utils/Antd';
import axios from '../../utils/axios';
import util from '../../utils/util';
import moment from 'moment';

import { SITE_TYPES as FACILITY_LEVELS } from '../../constants/facilityTypes';
const { TextArea } = require('antd').Input;
const { Option, OptGroup } = Select;

const STATUS_CFG = {
    scheduled:   { color:'#1890ff', label:'Scheduled',   bg:'#e6f4ff' },
    in_progress: { color:'#faad14', label:'In Progress', bg:'#fffbe6' },
    completed:   { color:'#52c41a', label:'Completed',   bg:'#f6ffed' },
    overdue:     { color:'#f5222d', label:'Overdue',     bg:'#fff2f0' },
    cancelled:   { color:'#bbb',    label:'Cancelled',   bg:'#f5f5f5' },
};


// Build a colour for a clause — fall back to its standard's accent
function clauseColor(clause, standards) {
    if (!clause) return '#1890ff';
    const std = standards.find(s => s.id === clause.standard_id);
    return std?.accent || '#1890ff';
}

// Quarters computed RELATIVE to the selected period's own period_start —
// identical logic to QoCCompliance.js's getQuarterRanges(). Keep these two
// in sync: if a period starts 01 Apr, Q1 = Apr-Jun, Q2 = Jul-Sep, etc.,
// matching whatever fiscal convention that period actually uses.
function getQuarterRanges(period) {
    if (!period?.period_start) return {};
    const start = moment(period.period_start).startOf('day');
    const ranges = {};
    ['Q1','Q2','Q3','Q4'].forEach((q, i) => {
        const qStart = start.clone().add(i*3, 'months');
        const qEnd   = start.clone().add((i+1)*3, 'months').subtract(1, 'day');
        ranges[q] = [qStart, qEnd];
    });
    return ranges;
}

// Duplicate check helper
function findDuplicates(existingSchedules, candidateRows) {
    const existingKeys = new Set(
        existingSchedules
            .filter(s => s.status !== 'cancelled')
            .map(s => `${s.facility_id}|${s.clause_id || ''}|${moment(s.scheduled_date).year()}`)
    );
    const dupes = [];
    for (const row of candidateRows) {
        const year = moment(row.scheduled_date).year();
        const key  = `${row.facility_id}|${row.clause_id || ''}|${year}`;
        if (existingKeys.has(key)) {
            dupes.push({
                facility_name: row.facility_name || row.facility_id,
                clause_code:   row.clause_code   || '(full audit)',
                year,
            });
        }
    }
    return dupes;
}

export default function QoCSchedules() {
    const [schedules,  setSchedules]  = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('qoc_schedules');
    const canEdit   = util.canEdit('qoc_schedules');
    const canDelete = util.canDelete('qoc_schedules');
    const canExport = util.canExport('qoc_schedules');
    const [summary,    setSummary]    = useState({});
    const [facilities, setFacilities] = useState([]);
    const [clauses,    setClauses]    = useState([]);
    const [standards,  setStandards]  = useState([]);   // NEW — for grouping
    const [users,      setUsers]      = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [filters,    setFilters]    = useState({ status:'', facility_id:'' });
    const isAdmin = (util.getModules?.()?.qoc_admin === 1) ||
                    parseInt(localStorage.getItem('is_admin') || '0') === 1;

    const [singleOpen,    setSingleOpen]    = useState(false);
    const [editSched,     setEditSched]     = useState(null);
    const [singleForm,    setSingleForm]    = useState({});

    const [bulkOpen,      setBulkOpen]      = useState(false);
    const [bulkFacilitySearch, setBulkFacilitySearch] = useState('');
    const [bulkForm,      setBulkForm]      = useState({
        selection_mode: 'level',
        facility_levels: [],
        level_region_filter: null,
        region: null,
        custom_facility_ids: [],
        clause_ids: [],
        scheduled_date: moment(),
        window_start: null,
        window_end: null,
        assigned_assessors: [],
        notes: '',
    });
    // Per-facility overrides — a facility only gets a key here once the user
    // explicitly edits ITS date or clauses via the per-row controls in the
    // preview panel. Until then, it keeps following the global date/clause
    // defaults above dynamically (see getFacDate/getFacClauses), so changing
    // the global fields still updates every facility that hasn't been
    // individually customized, without ever stomping on ones that have.
    const [facilityOverrides, setFacilityOverrides] = useState({});
    const [bulkSaving,     setBulkSaving]     = useState(false);
    const { periods: periodsList, currentPeriod: currentSchedPeriod } = usePeriods();

    // There's no raw user ID stored client-side at login (util only stores
    // name/token/modules/etc.) — so the only way to identify "the person
    // creating this schedule" from the client is to match their logged
    // name against the fetched users list. Case/whitespace-insensitive to
    // reduce false negatives from minor formatting differences.
    const myUserId = React.useMemo(() => {
        const myName = (util.getLoggedName() || '').trim().toLowerCase();
        if (!myName) return null;
        const match = users.find(u => (u.name || '').trim().toLowerCase() === myName);
        return match ? match.id : null;
    }, [users]);

    // Once the current user's id resolves, default them onto the bulk
    // form's Assessor(s) field — but only if nothing has been picked yet,
    // so this never overwrites a real selection.
    useEffect(() => {
        if (myUserId && (!bulkForm.assigned_assessors || bulkForm.assigned_assessors.length === 0)) {
            setBulkForm(f => ({ ...f, assigned_assessors: [myUserId] }));
        }
    }, [myUserId]);

    useEffect(() => {
        if (currentSchedPeriod) {
            setBulkForm(f => ({
                ...f,
                period_label:   currentSchedPeriod.label,
                window_end:     moment(currentSchedPeriod.period_end),
                window_start:   moment(currentSchedPeriod.period_start),
            }));
        }
    }, [currentSchedPeriod]);

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data||[]));
        // Load standards first, then clauses (so we can group)
        QoCService.getStandards().then(r => setStandards(r.data?.data||[]));
        QoCService.getClauses().then(r => setClauses(r.data?.data||[]));
        axios.get('qoc/assessors').then(r => {
            const raw = r.data?.data || [];
            setUsers(raw.map(u => ({
                ...u,
                label: u.name + (u.region ? ` (${u.region} Region)` : ' (National)'),
            })));
        });
        load();
    }, []);

    const load = async (overrideFilters) => {
        const af = overrideFilters || filters;
        setLoading(true);
        try {
            let res;
            if (isAdmin) {
                // Admins see all schedules and can manage them
                res = await QoCService.getAuditSchedules({
                    ...(af.status       && { status: af.status }),
                    ...(af.facility_id  && { facility_id: af.facility_id }),
                    ...(af.region       && { region: af.region }),
                    ...(af.standard_id  && { standard_id: af.standard_id }),
                    ...(af.clause_id    && { clause_id: af.clause_id }),
                    ...(af.period_label && { period_label: af.period_label }),
                });
                setSummary(res.data?.summary || {});
            } else {
                // Assessors only see schedules assigned to them — but this
                // endpoint takes no filter params at all, so every filter
                // has to be applied client-side below or the filter bar
                // would silently do nothing for non-admin users.
                res = await QoCService.getSchedulesForAssessor();
                setSummary({});
            }
            let data = res.data?.data || [];
            if (!isAdmin) {
                if (af.status)       data = data.filter(s => s.status === af.status);
                if (af.facility_id)  data = data.filter(s => String(s.facility_id) === String(af.facility_id));
                if (af.region)       data = data.filter(s => s.region === af.region);
                if (af.standard_id) {
                    const stdClauseIds = new Set(clauses.filter(c => String(c.standard_id) === String(af.standard_id)).map(c => c.id));
                    data = data.filter(s => stdClauseIds.has(s.clause_id));
                }
                if (af.clause_id)    data = data.filter(s => String(s.clause_id) === String(af.clause_id));
                if (af.period_label) data = data.filter(s => s.period_label === af.period_label);
            }
            setSchedules(data);
        } catch { message.error('Failed to load schedules'); }
        finally { setLoading(false); }
    };

    // Group clauses by standard for dropdowns
    const clausesByStandard = standards
        .filter(std => std.category !== 'Health Awards')
        .map(std => ({
        standard: std,
        clauses:  clauses.filter(c => String(c.standard_id) === String(std.id) && !c.code.startsWith('ENHEA')),
    })).filter(g => g.clauses.length > 0);

    // Clauses with no standard assigned (fallback group)
    const orphanClauses = clauses.filter(c => !c.standard_id || !standards.find(s => String(s.id) === String(c.standard_id)));

    // Render grouped clause options for a Select
    const renderClauseOptGroups = () => [
        ...clausesByStandard.map(({ standard, clauses: cls }) => (
            <OptGroup key={standard.id} label={
                <span style={{ fontWeight: 700, color: standard.accent || '#555' }}>
                    {standard.name}
                </span>
            }>
                {cls.map(c => (
                    <Option key={c.id} value={c.id}>
                        <span style={{ fontWeight: 700, color: standard.accent || '#1890ff', marginRight: 6 }}>
                            {c.code}
                        </span>
                        <span style={{ fontSize: 12, color: '#555' }}>{c.title}</span>
                    </Option>
                ))}
            </OptGroup>
        )),
        ...(orphanClauses.length > 0 ? [
            <OptGroup key="other" label="Other">
                {orphanClauses.map(c => (
                    <Option key={c.id} value={c.id}>{c.code} — {c.title}</Option>
                ))}
            </OptGroup>
        ] : []),
    ];

    const openNew  = () => {
        setEditSched(null);
        const initForm = {
            status:'scheduled', scheduled_date: moment(), assigned_assessors: myUserId ? [myUserId] : [],
            schedule_type: 'date', quarter: null, period_label: currentSchedPeriod?.label || null,
        };
        if (currentSchedPeriod) {
            initForm.window_start = moment(currentSchedPeriod.period_start);
            initForm.window_end   = moment(currentSchedPeriod.period_end);
        }
        setSingleForm(initForm);
        setSingleOpen(true);
    };
    const openEdit = (s) => {
        setEditSched(s);
        let assigned = [];
        try {
            assigned = s.assigned_assessors ? JSON.parse(s.assigned_assessors) : [];
            assigned = assigned.map(Number).filter(Boolean);
        } catch (e) { assigned = []; }
        setSingleForm({ ...s,
            scheduled_date:     s.scheduled_date ? moment(s.scheduled_date) : null,
            window_start:       s.window_start   ? moment(s.window_start)   : null,
            window_end:         s.window_end     ? moment(s.window_end)     : null,
            assigned_assessors: assigned,
            // Schedules created before quarterly targeting existed have
            // NULL here — treat them as plain specific-date schedules.
            schedule_type:      s.schedule_type || 'date',
            period_label:       s.period_label || currentSchedPeriod?.label || null,
        });
        setSingleOpen(true);
    };

    const handleSingleSave = async () => {
        if (!singleForm.facility_id || !singleForm.scheduled_date) {
            message.error('Facility and Scheduled Date are required');
            return;
        }
        if (!singleForm.assigned_assessors || singleForm.assigned_assessors.length === 0) {
            message.error('At least one assessor is required');
            return;
        }
        if (!editSched) {
            const facility  = facilities.find(f => f.id === singleForm.facility_id);
            const clause    = clauses.find(c => c.id === singleForm.clause_id);
            const candidate = [{
                facility_id:   singleForm.facility_id,
                facility_name: facility?.business_name || singleForm.facility_id,
                clause_id:     singleForm.clause_id || '',
                clause_code:   clause?.code || '(full audit)',
                scheduled_date: moment(singleForm.scheduled_date).format('YYYY-MM-DD'),
            }];
            const dupes = findDuplicates(schedules, candidate);
            if (dupes.length > 0) {
                const d = dupes[0];
                Modal.error({
                    title: 'Duplicate schedule',
                    content: (
                        <div>
                            <strong>{d.facility_name}</strong> already has a{' '}
                            <strong>{d.clause_code}</strong> schedule in{' '}
                            <strong>{d.year}</strong>.
                            <div style={{ marginTop:8, fontSize:12, color:'#888' }}>
                                Only one active schedule per facility per clause per calendar year is allowed.
                            </div>
                        </div>
                    ),
                });
                return;
            }
        }
        setLoading(true);
        try {
            await QoCService.saveAuditSchedule({
                ...singleForm,
                scheduled_date: moment(singleForm.scheduled_date).format('YYYY-MM-DD'),
                window_start: singleForm.window_start ? moment(singleForm.window_start).format('YYYY-MM-DD') : null,
                window_end:   singleForm.window_end   ? moment(singleForm.window_end).format('YYYY-MM-DD')   : null,
                // Derived, not user-set — there's no separate "Lead Assessor"
                // control anymore, just Assessor(s). The first person picked
                // becomes "lead" purely so the existing table column keeps
                // working, unrelated to permission gating.
                lead_assessor_id: (singleForm.assigned_assessors||[])[0] || null,
                assigned_assessors: JSON.stringify(singleForm.assigned_assessors || []),
            });
            const facilityName = facilities.find(f => f.id === singleForm.facility_id)?.business_name || String(singleForm.facility_id);
            const clauseCode   = clauses.find(c => c.id === singleForm.clause_id)?.code || 'Full Audit';
            editSched
                ? trailUpdate(TRAIL_MODULES.AUDIT_SCHEDULES, `${facilityName} — ${clauseCode}`, editSched.id,
                    { status: singleForm.status, date: moment(singleForm.scheduled_date).format('YYYY-MM-DD') })
                : trailCreate(TRAIL_MODULES.AUDIT_SCHEDULES, `${facilityName} — ${clauseCode}`, '',
                    { date: moment(singleForm.scheduled_date).format('YYYY-MM-DD') });
            message.success('Schedule saved');
            setSingleOpen(false);
            load();
        } catch { message.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        try {
            const sched = schedules.find(s => s.id === id);
            await QoCService.deleteAuditSchedule(id);
            trailDelete(TRAIL_MODULES.AUDIT_SCHEDULES,
                `${sched?.facility_name || id} — ${sched?.clause_code || 'Full Audit'}`, id,
                { status: sched?.status, date: sched?.scheduled_date });
            message.success('Deleted');
            load();
        } catch { message.error('Failed to delete'); }
    };

    // Facilities matched by the current selection mode — pure derivation
    // from bulkForm + facilities, recomputed automatically whenever either
    // changes (no manual "recompute preview" call needed anymore).
    const bulkFacilities = React.useMemo(() => {
        const mode = bulkForm.selection_mode || 'level';
        if (mode === 'level') {
            const levels = bulkForm.facility_levels || [];
            if (!levels.length) return [];
            return facilities.filter(f =>
                levels.includes(f.site_type) &&
                (!bulkForm.level_region_filter || (f.located_region||f.region) === bulkForm.level_region_filter)
            );
        }
        if (mode === 'region') {
            if (!bulkForm.region) return [];
            return facilities.filter(f => (f.located_region||f.region) === bulkForm.region);
        }
        if (mode === 'custom') {
            const ids = bulkForm.custom_facility_ids || [];
            if (!ids.length) return [];
            return facilities.filter(f => ids.includes(f.id));
        }
        return [];
    }, [bulkForm.selection_mode, bulkForm.facility_levels, bulkForm.level_region_filter, bulkForm.region, bulkForm.custom_facility_ids, facilities]);

    // Per-facility date/clauses — fall back to the shared global default
    // until this specific facility has been individually customized.
    const getFacDate    = (facId) => facilityOverrides[facId]?.scheduled_date ?? bulkForm.scheduled_date;
    const getFacClauses = (facId) => facilityOverrides[facId]?.clause_ids   ?? bulkForm.clause_ids;
    const setFacDate = (facId, date) => setFacilityOverrides(prev => ({
        ...prev, [facId]: { ...prev[facId], scheduled_date: date },
    }));
    const setFacClauses = (facId, ids) => setFacilityOverrides(prev => ({
        ...prev, [facId]: { ...prev[facId], clause_ids: ids },
    }));
    const clearFacOverride = (facId) => setFacilityOverrides(prev => {
        const next = { ...prev };
        delete next[facId];
        return next;
    });
    const facHasOverride = (facId) => !!facilityOverrides[facId];

    // The actual flat rows to save — one per (facility, clause) pair, each
    // using that facility's own date/clauses (default or overridden). This
    // is the only thing that changed shape-wise from the old flat
    // cross-product: previously every facility shared one date and one
    // clause list; now each facility resolves its own via getFacDate/
    // getFacClauses, but the OUTPUT shape sent to bulkSaveSchedules is
    // identical, so no backend change is needed.
    const bulkPreview = React.useMemo(() => {
        const rows = [];
        bulkFacilities.forEach(fac => {
            const facDate    = getFacDate(fac.id);
            const facClauses = getFacClauses(fac.id) || [];
            if (!facDate || !facClauses.length) return;
            // A facility with a literal per-row date override is, by
            // definition, a specific-date schedule for that row — even
            // while the shared batch default is a quarterly target.
            // Mirrors the same "a manual pick wins" rule used everywhere
            // else in this feature (single-schedule form, checklist
            // auto-suggest): an explicit override always beats the
            // computed default.
            const overridden = facHasOverride(fac.id);
            facClauses.forEach(cid => {
                const cl = clauses.find(c => c.id === cid);
                rows.push({
                    facility_id:        fac.id,
                    facility_name:      fac.business_name,
                    clause_id:          cid,
                    clause_code:        cl?.code,
                    clause_title:       cl?.title,
                    scheduled_date:     moment(facDate).format('YYYY-MM-DD'),
                    schedule_type:      overridden ? 'date' : (bulkForm.schedule_type || 'date'),
                    quarter:            overridden ? null   : (bulkForm.quarter || null),
                    period_label:       bulkForm.period_label || null,
                    window_start:       bulkForm.window_start ? moment(bulkForm.window_start).format('YYYY-MM-DD') : null,
                    window_end:         bulkForm.window_end   ? moment(bulkForm.window_end).format('YYYY-MM-DD')   : null,
                    lead_assessor_id:   (bulkForm.assigned_assessors||[])[0] || null,
                    assigned_assessors: JSON.stringify(bulkForm.assigned_assessors || []),
                    status:             'scheduled',
                    notes:              bulkForm.notes || '',
                });
            });
        });
        return rows;
    }, [bulkFacilities, facilityOverrides, bulkForm.scheduled_date, bulkForm.schedule_type, bulkForm.quarter,
        bulkForm.period_label, bulkForm.clause_ids,
        bulkForm.window_start, bulkForm.window_end, bulkForm.assigned_assessors, bulkForm.notes, clauses]);

    const setBulkField = (k, v) => setBulkForm(prev => ({ ...prev, [k]: v }));

    const handleBulkSave = async () => {
        if (!bulkPreview.length) { message.error('Nothing to schedule'); return; }
        if (!bulkForm.assigned_assessors || bulkForm.assigned_assessors.length === 0) {
            message.error('At least one assessor is required');
            return;
        }
        const dupes = findDuplicates(schedules, bulkPreview);
        if (dupes.length > 0) {
            Modal.confirm({
                title: `${dupes.length} duplicate schedule${dupes.length > 1 ? 's' : ''} detected`,
                icon: null,
                width: 560,
                content: (
                    <div>
                        <div style={{ marginBottom:10, color:'#555', fontSize:13 }}>
                            The following already have an active schedule for the same calendar year.
                            Duplicates will be <strong>skipped</strong>. Continue?
                        </div>
                        <div style={{ maxHeight:200, overflowY:'auto',
                            border:'1px solid #ffe0e0', borderRadius:6,
                            background:'#fff8f8', padding:'8px 12px' }}>
                            {dupes.map((d, i) => (
                                <div key={i} style={{ fontSize:12, color:'#f5222d',
                                    padding:'3px 0', borderBottom: i < dupes.length-1 ? '1px solid #ffe0e0' : 'none' }}>
                                    <strong>{d.facility_name}</strong> — {d.clause_code} — {d.year}
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop:10, fontSize:12, color:'#888' }}>
                            {bulkPreview.length - dupes.length} new schedule(s) will be created.
                        </div>
                    </div>
                ),
                okText: `Create ${bulkPreview.length - dupes.length} new schedule(s)`,
                okButtonProps: {
                    disabled: bulkPreview.length - dupes.length === 0,
                    style: { background:'#7c3aed', borderColor:'#7c3aed' }
                },
                cancelText: 'Cancel',
                onOk: () => doBulkSave(dupes),
            });
            return;
        }
        doBulkSave([]);
    };

    const doBulkSave = async (dupesToSkip) => {
        const dupeKeys = new Set(
            dupesToSkip.map(d => `${d.facility_name}|${d.clause_code}|${d.year}`)
        );
        const toSave = bulkPreview.filter(row => {
            const year = moment(row.scheduled_date).year();
            const key  = `${row.facility_name}|${row.clause_code || '(full audit)'}|${year}`;
            return !dupeKeys.has(key);
        });
        if (!toSave.length) { message.info('No new schedules to create.'); return; }
        setBulkSaving(true);
        try {
            const res = await QoCService.bulkSaveSchedules(toSave);
            const count = res.data?.data?.count || toSave.length;
            trailCreate(TRAIL_MODULES.AUDIT_SCHEDULES, 'Bulk Schedule', '',
                { count, facilityCount: bulkFacilities.length,
                  clauseCount: bulkForm.clause_ids?.length,
                  mode: bulkForm.selection_mode,
                  date: moment(bulkForm.scheduled_date).format('YYYY-MM-DD') });
            message.success(`Created ${count} schedules`);
            setBulkOpen(false);
            setBulkForm({ selection_mode:'level', facility_levels:[], level_region_filter:null, region:null,
                custom_facility_ids:[], clause_ids:[], scheduled_date:null, schedule_type:'date', quarter:null,
                window_start:null, window_end:null,
                assigned_assessors:[], notes:'' });
            setFacilityOverrides({});
            load();
        } catch (e) { message.error(e.response?.data?.message || 'Failed to save'); }
        finally { setBulkSaving(false); }
    };

    const today = moment().startOf('day');

    const columns = [
        {
            title: 'Facility', dataIndex: 'facility_name', ellipsis: true, width: 180,
            render: (name, r) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{name}</div>
                    <div style={{ fontSize: 10, color: '#888' }}>{r.site_type} • {r.region}</div>
                </div>
            )
        },
        {
            title: 'Clause', dataIndex: 'clause_code', width: 130,
            render: (code, r) => {
                if (!code) return <Tag>Full Audit</Tag>;
                const cl  = clauses.find(c => c.code === code);
                const col = clauseColor(cl, standards);
                return (
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <div style={{ width:28, height:22, borderRadius:4, background:col,
                            color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:9, fontWeight:800, padding:'0 4px', whiteSpace:'nowrap' }}>{code}</div>
                        <span style={{ fontSize:10, color:'#555' }}>{(r.clause_title||'').slice(0,16)}</span>
                    </div>
                );
            }
        },
        {
            title: 'Scheduled Date', dataIndex: 'scheduled_date', width: 120,
            render: (d, r) => {
                const overdue = r.status !== 'completed' && r.status !== 'cancelled'
                    && moment(d).isBefore(today);
                return (
                    <div>
                        {r.schedule_type === 'quarter' && r.quarter && (
                            <Tag color="purple" style={{ fontSize: 9, marginBottom: 2 }}>
                                {r.quarter}{r.period_label ? ` \u2014 ${r.period_label}` : ''}
                            </Tag>
                        )}
                        <div style={{ fontWeight: 600, fontSize: 12, color: overdue ? '#f5222d' : '#333' }}>
                            {r.schedule_type === 'quarter' ? 'Due ' : ''}{moment(d).format('DD MMM YYYY')}
                        </div>
                        {overdue && <Tag color="red" style={{ fontSize: 9 }}>
                            {today.diff(moment(d),'days')}d overdue
                        </Tag>}
                    </div>
                );
            }
        },
        {
            title: 'Window', width: 140,
            render: (_, r) => r.window_start ? (
                <span style={{ fontSize: 11, color: '#888' }}>
                    {moment(r.window_start).format('DD MMM')} → {r.window_end ? moment(r.window_end).format('DD MMM YY') : '—'}
                </span>
            ) : <span style={{ color: '#ccc' }}>—</span>
        },
        {
            title: 'Lead Assessor', dataIndex: 'lead_assessor_name', width: 130,
            render: v => v || <span style={{ color: '#ccc' }}>Unassigned</span>
        },
        {
            title: 'Status', dataIndex: 'status', width: 115,
            render: s => {
                const cfg = STATUS_CFG[s] || STATUS_CFG.scheduled;
                return <Tag color={cfg.color} style={{ fontWeight: 700, fontSize: 11 }}>{cfg.label}</Tag>;
            }
        },
        {
            title: 'Assessment', width: 100,
            render: (_, r) => {
                if (!r.assessment_id) return <span style={{ color:'#ccc', fontSize:11 }}>—</span>;
                return (
                    <div>
                        <span style={{ fontWeight:700, fontSize:12,
                            color: r.assessment_status==='approved'?'#52c41a':'#faad14' }}>
                            {parseFloat(r.assessment_score||0).toFixed(1)}%
                        </span>
                        <div style={{ fontSize:10, color:'#888' }}>{r.assessment_status}</div>
                    </div>
                );
            }
        },
        {
            title: '', width: 80,
            render: (_, r) => (
                <div style={{ display:'flex', gap:4 }}>
                    {canEdit && <Button size="small" onClick={() => openEdit(r)}>Edit</Button>}
                    {canDelete && <Button size="small" danger onClick={() => handleDelete(r.id)}>✕</Button>}
                </div>
            )
        },
    ];

    const overdueCount = schedules.filter(s =>
        s.status !== 'completed' && s.status !== 'cancelled'
        && moment(s.scheduled_date).isBefore(today)
    ).length;

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Audit Schedules</h2></div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                    {canAdd && <Button icon={<ThunderboltOutlined/>} type="primary"
                        style={{ background:'#7c3aed', borderColor:'#7c3aed' }}
                        onClick={() => setBulkOpen(true)}>
                        Bulk Schedule by Level
                    </Button>}
                    {canAdd && <Button icon={<PlusOutlined/>} type="primary" onClick={openNew}>
                        New Schedule
                    </Button>}
                </div>
            </div>

            <div className="page-pad">
                <Row gutter={12} style={{ marginBottom: 16 }}>
                    {[
                        { label:'Total',       val: schedules.length,                                    color:'#1890ff' },
                        { label:'Scheduled',   val: schedules.filter(s=>s.status==='scheduled').length,  color:'#06b6d4' },
                        { label:'In Progress', val: schedules.filter(s=>s.status==='in_progress').length,color:'#faad14' },
                        { label:'Completed',   val: schedules.filter(s=>s.status==='completed').length,  color:'#52c41a' },
                        { label:'Overdue',     val: overdueCount,                                        color:'#f5222d' },
                    ].map(s => (
                        <Col key={s.label} span={4}>
                            <div style={{ background:`${s.color}09`, border:`1px solid ${s.color}28`,
                                borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8 }}>{s.label}</div>
                                <div style={{ fontSize:28, fontWeight:900, color:s.color }}>{s.val}</div>
                            </div>
                        </Col>
                    ))}
                    <Col span={4}>
                        {schedules.length > 0 && (
                            <div style={{ background:'#f0f5ff', border:'1px solid #dbeafe',
                                borderRadius:10, padding:'12px 16px', textAlign:'center' }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8 }}>% Complete</div>
                                <Progress type="circle"
                                    percent={Math.round(schedules.filter(s=>s.status==='completed').length/schedules.length*100)}
                                    width={50} strokeColor="#52c41a"
                                    format={v=><span style={{ fontSize:12, fontWeight:800 }}>{v}%</span>}/>
                            </div>
                        )}
                    </Col>
                </Row>

                <Card size="small" style={{ marginBottom: 14 }}>
                    <Row gutter={8} align="middle">
                        <Col span={3}>
                            <Select allowClear placeholder="Region" style={{ width:'100%' }}
                                value={filters.region || undefined}
                                onChange={v => {
                                    const f2 = {...filters, region:v||''};
                                    // Drop a facility selection that no longer matches the new region
                                    if (v && f2.facility_id) {
                                        const fac = facilities.find(x => String(x.id) === String(f2.facility_id));
                                        if (!fac || (fac.located_region||fac.region) !== v) f2.facility_id = '';
                                    }
                                    setFilters(f2); setTimeout(()=>load(f2),50);
                                }}>
                                {[...new Set(facilities.map(f=>f.located_region||f.region).filter(Boolean))].sort().map(r => (
                                    <Option key={r} value={r}>{r}</Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select allowClear placeholder="Facility" style={{ width:'100%' }}
                                showSearch optionFilterProp="children"
                                value={filters.facility_id || undefined}
                                onChange={v => { const f2={...filters,facility_id:v||''}; setFilters(f2); setTimeout(()=>load(f2),50); }}>
                                {facilities
                                    .filter(f => !filters.region || (f.located_region||f.region) === filters.region)
                                    .map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select allowClear placeholder="Standard" style={{ width:'100%' }}
                                value={filters.standard_id || undefined}
                                onChange={v => { const f2={...filters,standard_id:v||'',clause_id:''}; setFilters(f2); setTimeout(()=>load(f2),50); }}>
                                {standards.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select allowClear placeholder="Clause" style={{ width:'100%' }}
                                showSearch optionFilterProp="children"
                                value={filters.clause_id || undefined}
                                onChange={v => { const f2={...filters,clause_id:v||''}; setFilters(f2); setTimeout(()=>load(f2),50); }}>
                                {(filters.standard_id
                                    ? clauses.filter(c => String(c.standard_id)===String(filters.standard_id))
                                    : clauses.filter(c => !c.code.startsWith('ENHEA'))
                                ).map(c => <Option key={c.id} value={c.id}>{c.code} - {c.title}</Option>)}
                            </Select>
                        </Col>
                        <Col span={3}>
                            <Select allowClear placeholder="Period" style={{ width:'100%' }}
                                value={filters.period_label || undefined}
                                onChange={v => { const f2={...filters,period_label:v||''}; setFilters(f2); setTimeout(()=>load(f2),50); }}>
                                {periodsList.map(p => <Option key={p.label} value={p.label}>{p.label}</Option>)}
                            </Select>
                        </Col>
                        <Col span={3}>
                            <Select allowClear placeholder="Status" style={{ width:'100%' }}
                                value={filters.status || undefined}
                                onChange={v => { const f2={...filters,status:v||''}; setFilters(f2); setTimeout(()=>load(f2),50); }}>
                                {Object.entries(STATUS_CFG).map(([k,v]) => <Option key={k} value={k}>{v.label}</Option>)}
                            </Select>
                        </Col>
                        <Col span={3}>
                            <Button onClick={() => { const f2={status:'',facility_id:'',region:'',standard_id:'',clause_id:'',period_label:''}; setFilters(f2); setTimeout(()=>load(f2),50); }}>Clear</Button>
                        </Col>
                    </Row>
                </Card>
                <Card size="small">
                    <Table columns={columns} dataSource={schedules} rowKey="id" loading={loading}
                        pagination={{ pageSize:20, showTotal:t=>`${t} schedules` }}
                        rowClassName={r => {
                            if (r.status==='completed') return '';
                            if (moment(r.scheduled_date).isBefore(today)) return 'ant-table-row-overdue';
                            return '';
                        }}/>
                </Card>
            </div>

            {/* ── Single schedule modal ── */}
            <Modal title={editSched ? 'Edit Schedule' : 'New Audit Schedule'}
                open={singleOpen} onCancel={() => setSingleOpen(false)}
                onOk={handleSingleSave} confirmLoading={loading} okText="Save" width={700}>
                <Row gutter={16}>
                    <Col span={12}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Facility *</label>
                        <AntdSelect showSearch options={facilities} value={singleForm.facility_id}
                            onChange={v => setSingleForm({...singleForm, facility_id:v})} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>
                            Clause <span style={{ fontWeight:400, color:'#aaa', fontSize:11 }}>(blank = full audit)</span>
                        </label>
                        {/* GROUPED by standard */}
                        <Select allowClear placeholder="Full Audit" value={singleForm.clause_id}
                            onChange={v => setSingleForm({...singleForm, clause_id:v})}
                            style={{ width:'100%' }} showSearch optionFilterProp="children">
                            {renderClauseOptGroups()}
                        </Select>
                    </Col>
                    <Col span={24} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Schedule By</label>
                        <Radio.Group
                            value={singleForm.schedule_type || 'date'}
                            onChange={e => {
                                const type = e.target.value;
                                if (type === 'date') {
                                    setSingleForm(f => ({ ...f, schedule_type:'date', quarter:null }));
                                } else {
                                    setSingleForm(f => ({
                                        ...f, schedule_type:'quarter',
                                        period_label: f.period_label || currentSchedPeriod?.label || null,
                                        quarter: null, scheduled_date: null,
                                    }));
                                }
                            }}>
                            <Radio value="date">Specific Date</Radio>
                            <Radio value="quarter">Quarterly Target</Radio>
                        </Radio.Group>
                    </Col>

                    {(!singleForm.schedule_type || singleForm.schedule_type === 'date') && (
                        <Col span={8} style={{ marginTop:12 }}>
                            <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Scheduled Date *</label>
                            <AntdDatepicker value={singleForm.scheduled_date}
                                disabledDate={d => d && d.isBefore(moment().startOf('day'))}
                                onChange={v => {
                                    if (!v) { setSingleForm({...singleForm, scheduled_date:null}); return; }
                                    const updates = { scheduled_date: v };
                                    if (!singleForm.window_start) updates.window_start = v;
                                    if (!singleForm.window_end) {
                                        const cur = currentSchedPeriod;
                                        updates.window_end = cur ? moment(cur.period_end) : moment(v).add(30,'days');
                                    }
                                    setSingleForm({...singleForm, ...updates});
                                }} style={{ width:'100%' }}/>
                        </Col>
                    )}

                    {singleForm.schedule_type === 'quarter' && (() => {
                        const selPeriod = periodsList.find(p => p.label === singleForm.period_label) || currentSchedPeriod;
                        const ranges = getQuarterRanges(selPeriod);
                        return (
                            <>
                                <Col span={8} style={{ marginTop:12 }}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Period *</label>
                                    <Select value={singleForm.period_label} style={{ width:'100%' }}
                                        placeholder="Select period"
                                        onChange={v => setSingleForm(f => ({ ...f, period_label:v, quarter:null, scheduled_date:null }))}>
                                        {periodsList.filter(p => !p.label.startsWith('ENHEA')).map(p => (
                                            <Option key={p.label} value={p.label}>
                                                {p.is_current == 1 ? '* ' : ''}{p.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={8} style={{ marginTop:12 }}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Quarter *</label>
                                    <Select value={singleForm.quarter} style={{ width:'100%' }}
                                        placeholder="Select quarter" disabled={!selPeriod}
                                        onChange={v => {
                                            const [qStart, qEnd] = ranges[v] || [];
                                            setSingleForm(f => ({
                                                ...f, quarter: v,
                                                scheduled_date: qEnd || null,
                                                window_start: f.window_start || qStart || (selPeriod ? moment(selPeriod.period_start) : null),
                                                window_end:   f.window_end   || (selPeriod ? moment(selPeriod.period_end) : qEnd),
                                            }));
                                        }}>
                                        {['Q1','Q2','Q3','Q4'].map(q => {
                                            const [qStart, qEnd] = ranges[q] || [];
                                            return (
                                                <Option key={q} value={q}>
                                                    {q}{qStart && qEnd ? ` (${qStart.format('MMM')}\u2013${qEnd.format('MMM YYYY')})` : ''}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Col>
                                <Col span={8} style={{ marginTop:12 }}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Due By</label>
                                    <div style={{ padding:'6px 11px', border:'1px solid #d9d9d9', borderRadius:6,
                                        background:'#fafafa', color: singleForm.scheduled_date ? '#333' : '#bbb' }}>
                                        {singleForm.scheduled_date ? moment(singleForm.scheduled_date).format('DD MMM YYYY') : 'Pick a quarter'}
                                    </div>
                                </Col>
                            </>
                        );
                    })()}

                    <Col span={8} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Window Start</label>
                        <AntdDatepicker value={singleForm.window_start}
                            onChange={v => setSingleForm({...singleForm, window_start:v})} style={{ width:'100%' }}/>
                    </Col>
                    <Col span={8} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Window End</label>
                        <AntdDatepicker value={singleForm.window_end}
                            onChange={v => setSingleForm({...singleForm, window_end:v})} style={{ width:'100%' }}/>
                    </Col>
                    {singleForm.scheduled_date && currentSchedPeriod && (
                        moment(singleForm.scheduled_date).isBefore(moment(currentSchedPeriod.period_start)) ||
                        moment(singleForm.scheduled_date).isAfter(moment(currentSchedPeriod.period_end))
                    ) && (
                        <Col span={24} style={{ marginTop:8 }}>
                            <Alert type="warning" showIcon
                                message={`Scheduled date falls outside the current period (${currentSchedPeriod.label}: ${moment(currentSchedPeriod.period_start).format('DD MMM YYYY')} – ${moment(currentSchedPeriod.period_end).format('DD MMM YYYY')})`}/>
                        </Col>
                    )}
                    <Col span={12} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Status</label>
                        <Select value={singleForm.status}
                            onChange={v => setSingleForm({...singleForm, status:v})} style={{ width:'100%' }}>
                            {Object.entries(STATUS_CFG).map(([k,v]) => <Option key={k} value={k}>{v.label}</Option>)}
                        </Select>
                    </Col>
                    <Col span={24} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>
                            Assessor(s) *
                            <span style={{ fontWeight:400, fontSize:11, color:'#888', marginLeft:8 }}>
                                All selected assessors will see this schedule on their Assessments page
                            </span>
                        </label>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Select assessor(s)"
                            style={{ width:'100%' }}
                            value={singleForm.assigned_assessors || []}
                            onChange={v => setSingleForm({...singleForm, assigned_assessors: v})}
                            optionFilterProp="label"
                            options={[...users].sort((a,b) => (a.label||a.name||'').localeCompare(b.label||b.name||'')).map(u => ({ value: u.id, label: u.label || u.name }))}
                        />
                    </Col>
                    <Col span={24} style={{ marginTop:12 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4 }}>Notes</label>
                        <TextArea rows={2} value={singleForm.notes}
                            onChange={e => setSingleForm({...singleForm, notes:e.target.value})}
                            placeholder="Optional notes..."/>
                    </Col>
                </Row>
            </Modal>

            {/* ── BULK SCHEDULE MODAL ── */}
            <Modal
                title={<span><ThunderboltOutlined style={{ color:'#7c3aed', marginRight:8 }}/>
                    Bulk Schedule Audit</span>}
                open={bulkOpen}
                onCancel={() => { setBulkOpen(false); setFacilityOverrides({}); }}
                onOk={handleBulkSave}
                confirmLoading={bulkSaving}
                okText={`Create ${bulkPreview.length} Schedules`}
                okButtonProps={{ disabled: bulkPreview.length === 0,
                    style:{ background:'#7c3aed', borderColor:'#7c3aed' } }}
                width={960}
                style={{ top:20 }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:6 }}>Schedule By *</label>
                                <Select style={{ width:'100%' }}
                                    value={bulkForm.selection_mode || 'level'}
                                    onChange={v => setBulkField('selection_mode', v)}>
                                    <Select.Option value="level">🏗 By Facility Level</Select.Option>
                                    <Select.Option value="region">🗺 By Region</Select.Option>
                                    <Select.Option value="custom">☑ Custom Selection</Select.Option>
                                </Select>
                            </div>

                            {(!bulkForm.selection_mode || bulkForm.selection_mode === 'level') && (
                                <div>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                        Facility Level(s) *
                                    </label>
                                    <Select mode="multiple" allowClear placeholder="Select level(s)" style={{ width:'100%' }}
                                        value={bulkForm.facility_levels || []}
                                        onChange={v => setBulkField('facility_levels', v)}>
                                        {FACILITY_LEVELS.map(l => (
                                            <Select.Option key={l} value={l}>
                                                {l}
                                                <span style={{ color:'#aaa', marginLeft:8, fontSize:11 }}>
                                                    ({facilities.filter(f=>f.site_type===l &&
                                                        (!bulkForm.level_region_filter || (f.located_region||f.region)===bulkForm.level_region_filter)
                                                    ).length} facilities)
                                                </span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <label style={{ fontWeight:600, display:'block', marginTop:10, marginBottom:4, fontSize:12 }}>
                                        Region <span style={{ fontWeight:400, color:'#888' }}>(optional — narrows the level(s) above to one region)</span>
                                    </label>
                                    <Select allowClear placeholder="All regions" style={{ width:'100%' }}
                                        value={bulkForm.level_region_filter || undefined}
                                        onChange={v => setBulkField('level_region_filter', v || null)}>
                                        {[...new Set(facilities.map(f=>f.located_region||f.region).filter(Boolean))].sort().map(r => (
                                            <Select.Option key={r} value={r}>{r}</Select.Option>
                                        ))}
                                    </Select>
                                    {bulkFacilities.length > 0 && (
                                        <div style={{ marginTop:6, fontSize:11, color:'#1890ff' }}>
                                            {bulkFacilities.length} facilities matched
                                            {bulkForm.level_region_filter && <> in {bulkForm.level_region_filter}</>}
                                        </div>
                                    )}
                                </div>
                            )}

                            {bulkForm.selection_mode === 'region' && (
                                <div>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Region *</label>
                                    <Select placeholder="Select Region" style={{ width:'100%' }}
                                        value={bulkForm.region}
                                        onChange={v => setBulkField('region', v)}>
                                        {[...new Set(facilities.map(f=>f.located_region||f.region).filter(Boolean))].sort().map(r => (
                                            <Select.Option key={r} value={r}>
                                                {r}
                                                <span style={{ color:'#aaa', marginLeft:8, fontSize:11 }}>
                                                    ({facilities.filter(f=>(f.located_region||f.region)===r).length} facilities)
                                                </span>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            )}

                            {bulkForm.selection_mode === 'custom' && (
                                <div>
                                    <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                        Select Facilities *
                                        <span style={{ fontWeight:400, fontSize:11, color:'#888', marginLeft:8 }}>
                                            ({(bulkForm.custom_facility_ids||[]).length} selected)
                                        </span>
                                    </label>
                                    <div style={{ display:'flex', gap:6, marginBottom:6, flexWrap:'wrap' }}>
                                        <Button size="small" onClick={() => setBulkField('custom_facility_ids', facilities.map(f=>f.id))}>Select All</Button>
                                        <Button size="small" onClick={() => setBulkField('custom_facility_ids', [])}>Clear</Button>
                                        <Select allowClear size="small" placeholder="Filter by level" style={{ width:140 }}
                                            onChange={v => {
                                                if (!v) return;
                                                const ids = facilities.filter(f=>f.site_type===v).map(f=>f.id);
                                                setBulkField('custom_facility_ids', ids);
                                            }}>
                                            {FACILITY_LEVELS.map(l => <Select.Option key={l} value={l}>{l}</Select.Option>)}
                                        </Select>
                                        <Select allowClear size="small" placeholder="Filter by region" style={{ width:150 }}
                                            onChange={v => {
                                                if (!v) return;
                                                const ids = facilities.filter(f=>(f.located_region||f.region)===v).map(f=>f.id);
                                                setBulkField('custom_facility_ids', ids);
                                            }}>
                                            {[...new Set(facilities.map(f=>f.located_region||f.region).filter(Boolean))].sort().map(r => (
                                                <Select.Option key={r} value={r}>{r}</Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                    <Input allowClear size="small" placeholder="Search facility by name..."
                                        style={{ marginBottom:6 }}
                                        value={bulkFacilitySearch}
                                        onChange={e => setBulkFacilitySearch(e.target.value)} />
                                    <div style={{ maxHeight:180, overflowY:'auto',
                                        border:'1px solid #e8e8e8', borderRadius:6, padding:'4px 0' }}>
                                        <Checkbox.Group
                                            style={{ display:'flex', flexDirection:'column' }}
                                            value={bulkForm.custom_facility_ids||[]}
                                            onChange={v => setBulkField('custom_facility_ids', v)}>
                                            {facilities.filter(f => !bulkFacilitySearch || f.business_name.toLowerCase().includes(bulkFacilitySearch.toLowerCase())).map(f => (
                                                <Checkbox key={f.id} value={f.id}
                                                    style={{ padding:'4px 10px', fontSize:11,
                                                        borderBottom:'1px solid #f5f5f5', margin:0 }}>
                                                    <span style={{ fontWeight:600 }}>{f.business_name}</span>
                                                    <span style={{ color:'#aaa', fontSize:10, marginLeft:6 }}>
                                                        {f.site_type} • {f.located_region||f.region}
                                                    </span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                </div>
                            )}

                            {/* Clauses grouped by standard */}
                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                    Clauses to Schedule *
                                </label>
                                <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                                    <Button size="small" onClick={() => setBulkField('clause_ids', clauses.map(c=>c.id))}>All Clauses</Button>
                                    <Button size="small" onClick={() => setBulkField('clause_ids', [])}>Clear</Button>
                                </div>
                                {clausesByStandard.map(({ standard, clauses: cls }) => (
                                    <div key={standard.id} style={{ marginBottom:10 }}>
                                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                                            <div style={{ fontSize:11, fontWeight:700, color: standard.accent || '#555',
                                                textTransform:'uppercase', letterSpacing:0.5 }}>
                                                {standard.name}
                                            </div>
                                            <Button size="small" style={{ fontSize:10, height:20, padding:'0 6px' }}
                                                onClick={() => {
                                                    const otherIds = (bulkForm.clause_ids || []).filter(id => !cls.find(c => c.id === id));
                                                    setBulkField('clause_ids', [...otherIds, ...cls.map(c => c.id)]);
                                                }}>
                                                Select all
                                            </Button>
                                        </div>
                                        <Checkbox.Group
                                            style={{ display:'flex', flexDirection:'column', gap:4 }}
                                            value={bulkForm.clause_ids}
                                            onChange={v => {
                                                // Merge this standard's selection with others
                                                const otherStdIds = (bulkForm.clause_ids || [])
                                                    .filter(id => !cls.find(c => c.id === id));
                                                setBulkField('clause_ids', [...otherStdIds, ...v]);
                                            }}>
                                            {cls.map(c => (
                                                <Checkbox key={c.id} value={c.id}>
                                                    <span style={{ fontWeight:700, color: standard.accent || '#1890ff', marginRight:4 }}>{c.code}</span>
                                                    <span style={{ fontSize:11, color:'#555' }}>{c.title?.slice(0,34)}</span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                ))}
                                {orphanClauses.length > 0 && (
                                    <div>
                                        <div style={{ fontSize:11, fontWeight:700, color:'#888', marginBottom:4 }}>Other</div>
                                        <Checkbox.Group
                                            style={{ display:'flex', flexDirection:'column', gap:4 }}
                                            value={bulkForm.clause_ids}
                                            onChange={v => setBulkField('clause_ids', v)}>
                                            {orphanClauses.map(c => (
                                                <Checkbox key={c.id} value={c.id}>
                                                    <span style={{ fontSize:11 }}>{c.code} — {c.title?.slice(0,34)}</span>
                                                </Checkbox>
                                            ))}
                                        </Checkbox.Group>
                                    </div>
                                )}
                            </div>

                            {/* Period selector — auto-fills window; also the anchor period for quarterly targeting below */}
                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Assessment Period</label>
                                <Select allowClear placeholder="Select period (auto-fills window)" style={{ width:'100%' }}
                                    value={bulkForm.period_label}
                                    onChange={v => {
                                        const p = periodsList.find(x => x.label === v);
                                        setBulkForm(f => ({
                                            ...f,
                                            period_label:   v || null,
                                            window_start:   p ? moment(p.period_start) : f.window_start,
                                            window_end:     p ? moment(p.period_end)   : f.window_end,
                                            // Changing period invalidates a previously-picked quarter's dates
                                            ...(f.schedule_type === 'quarter' ? { quarter: null, scheduled_date: null } : {}),
                                        }));
                                    }}>
                                    {periodsList.filter(p => !p.label.startsWith('ENHEA')).map(p => (
                                        <Select.Option key={p.label} value={p.label}>
                                            {p.is_current == 1 ? '* ' : ''}{p.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:6 }}>Schedule By (default for this batch)</label>
                                <Radio.Group
                                    value={bulkForm.schedule_type || 'date'}
                                    onChange={e => {
                                        const type = e.target.value;
                                        if (type === 'date') {
                                            setBulkForm(f => ({ ...f, schedule_type:'date', quarter:null }));
                                        } else {
                                            setBulkForm(f => ({ ...f, schedule_type:'quarter', quarter:null, scheduled_date:null }));
                                        }
                                    }}>
                                    <Radio value="date">Specific Date</Radio>
                                    <Radio value="quarter">Quarterly Target</Radio>
                                </Radio.Group>
                                <div style={{ fontSize:11, color:'#888', marginTop:4 }}>
                                    Applies to every facility below that hasn't been individually customized
                                    with its own date in the preview panel.
                                </div>
                            </div>

                            {bulkForm.schedule_type === 'quarter' ? (() => {
                                const selPeriod = periodsList.find(p => p.label === bulkForm.period_label);
                                const ranges = getQuarterRanges(selPeriod);
                                return (
                                    <div>
                                        <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>Quarter *</label>
                                        <Select value={bulkForm.quarter} style={{ width:'100%' }}
                                            placeholder={selPeriod ? 'Select quarter' : 'Select an Assessment Period first'}
                                            disabled={!selPeriod}
                                            onChange={v => {
                                                const [, qEnd] = ranges[v] || [];
                                                setBulkForm(f => ({ ...f, quarter: v, scheduled_date: qEnd || null }));
                                            }}>
                                            {['Q1','Q2','Q3','Q4'].map(q => {
                                                const [qStart, qEnd] = ranges[q] || [];
                                                return (
                                                    <Select.Option key={q} value={q}>
                                                        {q}{qStart && qEnd ? ` (${qStart.format('MMM')}\u2013${qEnd.format('MMM YYYY')})` : ''}
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                        <div style={{ marginTop:6, fontSize:11, color:'#888' }}>
                                            Due by: <strong>{bulkForm.scheduled_date ? moment(bulkForm.scheduled_date).format('DD MMM YYYY') : '—'}</strong>
                                        </div>
                                    </div>
                                );
                            })() : null}

                            <Row gutter={8}>
                                <Col span={12}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>
                                        Window Start
                                        {bulkForm.schedule_type !== 'quarter' && (
                                            <span style={{ fontWeight:400, color:'#888', marginLeft:6 }}>(shared default date)</span>
                                        )}
                                    </label>
                                    <AntdDatepicker value={bulkForm.window_start} style={{ width:'100%' }}
                                        disabledDate={d => d && d.isBefore(moment().startOf('day'))}
                                        onChange={v => bulkForm.schedule_type === 'quarter'
                                            ? setBulkField('window_start', v)
                                            : setBulkForm(prev => ({ ...prev, window_start:v, scheduled_date:v }))}/>
                                </Col>
                                <Col span={12}>
                                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Window End</label>
                                    <AntdDatepicker value={bulkForm.window_end} style={{ width:'100%' }}
                                        onChange={v => setBulkField('window_end', v)}/>
                                </Col>
                            </Row>

                            {(() => {
                                const selPeriod = periodsList.find(p => p.label === bulkForm.period_label);
                                if (!selPeriod || !bulkForm.scheduled_date) return null;
                                const outside = moment(bulkForm.scheduled_date).isBefore(moment(selPeriod.period_start)) ||
                                                 moment(bulkForm.scheduled_date).isAfter(moment(selPeriod.period_end));
                                if (!outside) return null;
                                return (
                                    <Alert type="warning" showIcon style={{ marginTop:8 }}
                                        message={`Default date falls outside ${selPeriod.label} (${moment(selPeriod.period_start).format('DD MMM YYYY')} – ${moment(selPeriod.period_end).format('DD MMM YYYY')})`}/>
                                );
                            })()}

                            <div>
                                <label style={{ fontWeight:700, display:'block', marginBottom:4 }}>
                                    Assessor(s) *
                                    <span style={{ fontWeight:400, fontSize:11, color:'#888', marginLeft:8 }}>
                                        Applied to every schedule created in this batch
                                    </span>
                                </label>
                                <Select
                                    mode="multiple" allowClear placeholder="Select assessor(s)" style={{ width:'100%' }}
                                    value={bulkForm.assigned_assessors || []}
                                    onChange={v => setBulkField('assigned_assessors', v)}
                                    optionFilterProp="label"
                                    options={[...users].sort((a,b) => (a.label||a.name||'').localeCompare(b.label||b.name||'')).map(u => ({ value: u.id, label: u.label || u.name }))}
                                />
                            </div>

                            <div>
                                <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Notes (applied to all)</label>
                                <TextArea rows={2} value={bulkForm.notes}
                                    onChange={e => setBulkField('notes', e.target.value)}
                                    placeholder="Optional notes..."/>
                            </div>
                        </div>
                    </Col>

                    <Col span={12}>
                        <div style={{ background:'#f8faff', borderRadius:8, padding:14,
                            border:'1px solid #e0eaff', height:'100%' }}>
                            <div style={{ fontWeight:800, fontSize:13, marginBottom:4,
                                display:'flex', alignItems:'center', gap:8 }}>
                                <CopyOutlined style={{ color:'#7c3aed' }}/>
                                Preview — {bulkPreview.length} schedules to create
                            </div>
                            <div style={{ fontSize:11, color:'#888', marginBottom:10 }}>
                                Each facility defaults to the shared date/clauses on the left — click into either
                                field below to customize just that facility.
                            </div>
                            {bulkFacilities.length === 0
                                ? <div style={{ color:'#aaa', fontSize:12, textAlign:'center', padding:30 }}>
                                    Select facilities to preview
                                  </div>
                                : (
                                    <>
                                        <div style={{ marginBottom:10, fontSize:12 }}>
                                            <strong>{bulkFacilities.filter(f=>(getFacClauses(f.id)||[]).length>0).length}</strong> of{' '}
                                            <strong>{bulkFacilities.length}</strong> facilities will be scheduled →{' '}
                                            <strong style={{ color:'#7c3aed' }}>{bulkPreview.length}</strong> schedules total
                                            {bulkFacilities.some(f=>(getFacClauses(f.id)||[]).length===0) && (
                                                <span style={{ color:'#aaa' }}> ({bulkFacilities.filter(f=>(getFacClauses(f.id)||[]).length===0).length} skipped — no clauses)</span>
                                            )}
                                        </div>
                                        <div style={{ maxHeight:640, overflowY:'auto' }}>
                                            {bulkFacilities.map(fac => {
                                                const facDate     = getFacDate(fac.id);
                                                const facClauses  = getFacClauses(fac.id) || [];
                                                const isOverridden = facHasOverride(fac.id);
                                                const willBeSkipped = facClauses.length === 0;
                                                return (
                                                    <div key={fac.id} style={{ marginBottom:8,
                                                        background: willBeSkipped ? '#fafafa' : '#fff', borderRadius:6, padding:'8px 10px',
                                                        opacity: willBeSkipped ? 0.65 : 1,
                                                        border: willBeSkipped ? '1px dashed #d9d9d9' : isOverridden ? '1px solid #b39ddb' : '1px solid #e8eeff' }}>
                                                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                                                            <div style={{ fontWeight:700, fontSize:12, color:'#333' }}>
                                                                {fac.business_name}
                                                                <span style={{ fontWeight:400, color:'#aaa', fontSize:10, marginLeft:6 }}>
                                                                    {fac.site_type} · {fac.located_region||fac.region}
                                                                </span>
                                                            </div>
                                                            {willBeSkipped ? (
                                                                <Tag color="default" style={{ fontSize:9, margin:0 }}>Skipped — no clauses</Tag>
                                                            ) : isOverridden && (
                                                                <Tooltip title="Reset this facility back to the shared date/clauses">
                                                                    <Button size="small" type="link" style={{ fontSize:10, padding:0, height:'auto' }}
                                                                        onClick={() => clearFacOverride(fac.id)}>
                                                                        Reset
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                        <div style={{ display:'flex', gap:6, marginBottom:6, alignItems:'center' }}>
                                                            <Tooltip title={bulkForm.schedule_type === 'quarter'
                                                                ? 'Picking a date here overrides the quarterly target for this facility only — it becomes a specific-date schedule.'
                                                                : undefined}>
                                                                <AntdDatepicker size="small" style={{ width:130 }}
                                                                    value={facDate}
                                                                    disabledDate={d => d && d.isBefore(moment().startOf('day'))}
                                                                    onChange={v => setFacDate(fac.id, v)}/>
                                                            </Tooltip>
                                                            {isOverridden && bulkForm.schedule_type === 'quarter' && (
                                                                <Tag color="blue" style={{ fontSize:9, margin:0 }}>Specific date</Tag>
                                                            )}
                                                            {(() => {
                                                                const selPeriod = periodsList.find(p => p.label === bulkForm.period_label);
                                                                if (!selPeriod || !facDate) return null;
                                                                const outside = moment(facDate).isBefore(moment(selPeriod.period_start)) ||
                                                                                 moment(facDate).isAfter(moment(selPeriod.period_end));
                                                                if (!outside) return null;
                                                                return (
                                                                    <Tooltip title={`Outside ${selPeriod.label} (${moment(selPeriod.period_start).format('DD MMM YYYY')} – ${moment(selPeriod.period_end).format('DD MMM YYYY')})`}>
                                                                        <Tag color="warning" style={{ fontSize:9, margin:0 }}>⚠ Outside period</Tag>
                                                                    </Tooltip>
                                                                );
                                                            })()}
                                                        </div>
                                                        <Select
                                                            mode="multiple" size="small" allowClear
                                                            placeholder="No clauses selected — this facility will be skipped"
                                                            style={{ width:'100%' }}
                                                            value={facClauses}
                                                            onChange={v => setFacClauses(fac.id, v)}
                                                            optionFilterProp="label"
                                                            options={clauses.map(c => ({
                                                                value: c.id,
                                                                label: `${c.code} — ${c.title?.slice(0,30)||''}`,
                                                            }))}
                                                            tagRender={({ label, value, closable, onClose }) => {
                                                                const cl  = clauses.find(c=>c.id===value);
                                                                const col = clauseColor(cl, standards);
                                                                return (
                                                                    <Tag closable={closable} onClose={onClose}
                                                                        style={{ background:col, color:'#fff', border:'none',
                                                                            fontSize:9, fontWeight:700, marginInlineEnd:3 }}>
                                                                        {cl?.code || label}
                                                                    </Tag>
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <Divider style={{ margin:'10px 0' }}/>
                                        <div style={{ fontSize:11, color:'#888' }}>
                                            {bulkForm.schedule_type === 'quarter'
                                                ? <>Shared default: <strong>{bulkForm.quarter || '—'}{bulkForm.period_label ? ` — ${bulkForm.period_label}` : ''}</strong>
                                                    {bulkForm.scheduled_date && <> (due <strong>{moment(bulkForm.scheduled_date).format('DD MMM YYYY')}</strong>)</>}</>
                                                : <>Shared default date: <strong>{bulkForm.scheduled_date ? moment(bulkForm.scheduled_date).format('DD MMM YYYY') : '—'}</strong></>
                                            }
                                            {bulkForm.window_end && <> | Window ends: <strong>{moment(bulkForm.window_end).format('DD MMM YYYY')}</strong></>}
                                        </div>
                                    </>
                                )}
                        </div>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}





